import { SOFTPHONE_ROLES, _broadcastState } from '@/store/modules/softphone.store'

/**
 * Broadcasts the owner's softphone state to every other dashboard tab.
 *
 * This is a mutation subscriber rather than a `channel.postState()` at the end of each action
 * deliberately: the set of actions that move the state machine grows (answer, hang up, wrap-up
 * expiry, transport errors, a lost tab claim), and a single missed call site would leave the
 * campaigns list showing a stale button forever. Subscribing to the mutations makes that
 * impossible to forget.
 *
 * `APPLY_REMOTE_STATE` is skipped for the obvious reason - rebroadcasting what we were just told
 * would be an echo. It can only ever run on a mirror anyway, which never broadcasts.
 *
 * `SET_RESUME_PROMPT` is skipped because the prompt is not in the snapshot: it belongs to the one
 * tab that inherited the softphone, and broadcasting on it would be a state message that says
 * nothing new. `SET_TOKEN_EXPIRES_AT` is skipped for the same reason - only the owner holds a
 * token, and only the owner may re-mint one. `SET_REPORTING_SALE` is the clicking tab's own request
 * in flight, also kept out of the snapshot.
 *
 * `SET_CALL_BUYING_LIMITS` is deliberately NOT in the list, unlike the two above. It started out
 * skipped, on the reasoning that every tab watches the same billing state and reaches the same
 * answer for itself - but only the OWNER polls the live campaign's budget, so a mirror cannot work
 * that half out, and it is what decides which page the blocked notice sends the agent to. The
 * limits ride the snapshot for that reason, so the mutation that changes them has to broadcast it.
 */
export const NON_BROADCAST_MUTATIONS = [
  'softphone/APPLY_REMOTE_STATE',
  'softphone/SET_ROLE',
  'softphone/SET_RESUME_PROMPT',
  'softphone/SET_TOKEN_EXPIRES_AT',
  'softphone/SET_REPORTING_SALE'
]

export default function softphoneSyncPlugin(store) {
  store.subscribe(mutation => {
    if (!mutation.type.startsWith('softphone/')) {
      return
    }

    if (NON_BROADCAST_MUTATIONS.includes(mutation.type)) {
      return
    }

    if (store.state.softphone.role !== SOFTPHONE_ROLES.OWNER) {
      return
    }

    _broadcastState(store.state.softphone)
  })
}
